import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  alpha,
  InputAdornment,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { inputRootSx, FORM_NEON, FORM_TEXT_BODY } from '../../common/formFieldSx';

const checkboxSx = {
  color: alpha('#fff', 0.4),
  '&.Mui-checked': { color: '#fff' },
  '& .MuiSvgIcon-root': { fontSize: 18 },
};

const AudiencePickerColumn = ({
  title,
  icon,
  accentColor = FORM_NEON,
  items = [],
  selectedIds = [],
  onToggle,
  onSelectAll,
  onClear,
  disabled = false,
}) => {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => (item.name || '').toLowerCase().includes(q));
  }, [items, query]);

  const selectedSet = useMemo(
    () => new Set((selectedIds || []).map((id) => Number(id))),
    [selectedIds]
  );

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((item) => selectedSet.has(Number(item.id)));

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 300,
        borderRadius: '12px',
        border: `1px solid ${alpha(accentColor, 0.22)}`,
        background: alpha(accentColor, 0.04),
        overflow: 'hidden',
        opacity: disabled ? 0.45 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
        transition: 'opacity 0.25s ease',
      }}
    >
      <Box
        sx={{
          px: 1.75,
          py: 1.25,
          borderBottom: `1px solid ${alpha(accentColor, 0.15)}`,
          background: alpha(accentColor, 0.06),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          {icon}
          <Typography
            sx={{
              fontFamily: "'Rajdhani', sans-serif",
              fontWeight: 700,
              fontSize: '0.92rem',
              color: accentColor,
              letterSpacing: 0.3,
            }}
          >
            {title}
          </Typography>
          <Typography
            component="span"
            sx={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: '0.62rem',
              fontWeight: 600,
              color: alpha(accentColor, 0.75),
              bgcolor: alpha(accentColor, 0.12),
              border: `1px solid ${alpha(accentColor, 0.25)}`,
              borderRadius: '6px',
              px: 0.75,
              py: 0.2,
            }}
          >
            {selectedSet.size}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
          <Button
            size="small"
            onClick={() => onSelectAll?.(filtered.map((i) => i.id))}
            disabled={disabled || filtered.length === 0 || allFilteredSelected}
            sx={{
              minWidth: 0,
              px: 1,
              fontSize: '0.68rem',
              color: alpha(accentColor, 0.9),
              borderColor: alpha(accentColor, 0.3),
            }}
            variant="outlined"
          >
            All
          </Button>
          <Button
            size="small"
            onClick={onClear}
            disabled={disabled || selectedSet.size === 0}
            sx={{
              minWidth: 0,
              px: 1,
              fontSize: '0.68rem',
              color: alpha(FORM_TEXT_BODY, 0.6),
              borderColor: alpha(FORM_TEXT_BODY, 0.2),
            }}
            variant="outlined"
          >
            Clear
          </Button>
        </Box>
      </Box>

      <Box sx={{ px: 1.5, pt: 1.25, pb: 0.75 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={`Search ${title.toLowerCase()}…`}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: alpha(FORM_NEON, 0.45) }} />
              </InputAdornment>
            ),
          }}
          sx={inputRootSx}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 1,
          pb: 1.25,
          maxHeight: 220,
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': {
            background: alpha(accentColor, 0.25),
            borderRadius: 4,
          },
        }}
      >
        {filtered.length === 0 ? (
          <Typography sx={{ fontSize: '0.8rem', color: alpha(FORM_TEXT_BODY, 0.45), py: 2, textAlign: 'center' }}>
            {query ? 'No matches' : 'No items'}
          </Typography>
        ) : (
          filtered.map((item) => {
            const checked = selectedSet.has(Number(item.id));
            return (
              <FormControlLabel
                key={item.id}
                sx={{
                  display: 'flex',
                  mx: 0,
                  px: 1,
                  py: 0.35,
                  borderRadius: '8px',
                  transition: 'background 0.15s ease',
                  '&:hover': { bgcolor: alpha(accentColor, 0.08) },
                }}
                control={
                  <Checkbox
                    checked={checked}
                    onChange={() => onToggle(item.id)}
                    sx={checkboxSx}
                  />
                }
                label={
                  <Typography sx={{ fontSize: '0.84rem', color: alpha(FORM_TEXT_BODY, 0.88) }}>
                    {item.name}
                  </Typography>
                }
              />
            );
          })
        )}
      </Box>
    </Box>
  );
};

export default AudiencePickerColumn;
